import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import GrantActivationPanel from '@devnw/cyber-iam/iam/GrantActivationPanel/GrantActivationPanel'
import { useSelfServiceAccessRequestsApi } from '../api/accessRequestsClient'
import type { AccessRequest } from '../api/types'
import { toIamAccessRequest } from '../api/accessRequestMapping'
import { useGrantActivationCallbacks } from '../auth/useGrantActivationCallbacks'
import { PamLoading, PamError, PamEmpty } from '../components/PamStateComponents'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Failed to load access request'
}

const ScreenContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => ut(asUnified(theme)).space('md')};
`

const CardShell = styled.div`
  width: 100%;
  max-width: 480px;
`

/**
 * The single-purpose screen for /admin/activate/:requestId — reached ONLY
 * via a hand-off from another app (social's "Activate (secure step-up)"
 * button, PamActivateStepUp.tsx) asking the user to activate one specific,
 * already-approved grant. Deliberately shows NOTHING else: no breadcrumbs,
 * no decision/approve-deny card, no evidence link, no extend section — every
 * one of those belongs to the full admin-console AccessRequestDetail page,
 * not to a cross-app hand-off whose only possible action is activating this
 * grant.
 *
 * OIDCPAM Gate-6 Phase 5a: migrated off MUI onto
 * @devnw/cyber-iam/iam/GrantActivationPanel, which now owns the entire
 * "register a passkey first if needed -> authenticate -> retry" chain and
 * its phase-driven status/error UI as one continuous interaction (the same
 * guarantee the pre-migration useActivateWithStepUp hook provided: no
 * separate page for MFA, no manual second "Activate" click).
 *
 * Origin production hardening (PRESERVED): this cross-app hand-off activates
 * (and loads the request) through the ownership-checked SELF-SERVICE surface
 * (`useSelfServiceAccessRequestsApi` -> /v1/pam/me, NO tenant, NO admin
 * role) so the bare page can never read or activate another subject's
 * request — NOT the tenant-scoped admin API. This page still owns the API
 * call to load the request, the DPoP-on-activate transport (generated inside
 * the self-service activate, wired via useGrantActivationCallbacks), and the
 * return_to redirect.
 */
export const ActivateGrant = (): JSX.Element => {
  const { requestId } = useParams<{ requestId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  // Set by social's PamActivateStepUp hand-off (oidcSelfServiceActivationUrl)
  // so a successful activation can redirect the browser back to the app that
  // opened this tab. Validated server-side (isRegisteredRedirectOrigin) by
  // the /pam/activation-return endpoint this redirects through — never
  // trusted as-is here.
  const returnTo = searchParams.get('return_to')
  const accessRequestsApi = useSelfServiceAccessRequestsApi()
  const [req, setReq] = useState<AccessRequest | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  // Self-service activation (no tenant). GrantActivationPanel's internal
  // useStepUpActivation runs the register-if-needed -> authenticate -> retry
  // ceremony against this exact call; the 401 step-up sentinel + DPoP proof
  // live inside useSelfServiceAccessRequestsApi.activate (unchanged).
  const activateRequest = useCallback(async (): Promise<void> => {
    if (!requestId) throw new Error('Missing access request ID')
    await accessRequestsApi.activate(requestId)
  }, [accessRequestsApi, requestId])
  const activationCallbacks = useGrantActivationCallbacks(activateRequest)

  useEffect(() => {
    if (!requestId) return
    setLoading(true)
    accessRequestsApi
      .get(requestId)
      .then(setReq)
      .catch((e: unknown) => setLoadError(errMessage(e)))
      .finally(() => setLoading(false))
  }, [requestId, accessRequestsApi])

  // Only runs the redirect; the panel itself renders the "Grant activated"
  // success note (phase === 'done') before this fires.
  const handleActivated = (): void => {
    if (returnTo) {
      const dest =
        `${window.location.origin}/pam/activation-return?return_to=` +
        encodeURIComponent(returnTo)
      window.location.assign(dest)
    }
  }

  return (
    <ScreenContainer>
      <CardShell>
        {loading && <PamLoading />}
        {!loading && loadError && <PamError message={loadError} />}
        {!loading && !loadError && req && (
          <GrantActivationPanel
            testID="activate-grant"
            request={toIamAccessRequest(req)}
            callbacks={activationCallbacks}
            onActivated={handleActivated}
            onNavigateToPolicies={(policyLink) => navigate(policyLink)}
          />
        )}
        {!loading && !loadError && !req && (
          <PamEmpty message="Request not found" />
        )}
      </CardShell>
    </ScreenContainer>
  )
}
